// General import
import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import ReactQuill from 'react-quill'
import * as d3 from 'd3'
import {
  Col,
  Form,
  FormGroup,
  Popover,
  Row,
  Toast,
} from 'react-bootstrap'
import i18next, { t } from 'i18next'
import { ChakraProvider } from '@chakra-ui/react'

// Import from SankeySuite
import {
  SankeyAppTypes,
  SankeySuiteShowMenuComponentsType,
  suiteApplicationDataType,
  SuiteComponentUpdaterType,
  SuiteLinkFunctionTypes,
  SuiteMenuDraggableFType,
  SuiteNodesFunctionTypes,
  SuiteRedrawNodesFType,
  SuiteSankeyData,
  SuiteSankeyLink,
  SuiteSankeyNode,
  SuiteUiElementsRefType,
} from './import/Types'

// Import from OpenSankey
import { opensankey_theme } from 'open-sankey/src/chakra/Theme'
import {
  CreateLinksOnSVGFType,
  MenuTypes,
  RedrawNodesFType,
  SankeyData,
  SankeyLink,
  SankeyNode,
  agregationType,
  applicationDrawType,
  callbackFuncType,
  drawNodesFType,
  processFunctionsType
} from 'open-sankey/src/types/Types'
import { SankeyDrawTypes } from 'open-sankey/src/draw/types/SankeyDrawTypes'
import {
  addAllDropDownNode,
  AddDrawLinksEvent,
  AdjustSankeyZone,
  applyZoomEvent,
  convert_data,
  DeleteGNodes,
  drawAddLinks,
  drawAddNodes,
  DrawAllLinks,
  DrawAllNodes,
  DrawGrid,
  DrawLegend,
  drawLinkShape,
  DrawLinkStartSabot,
  GetLinkValue,
  GetSankeyMinWidthAndHeight,
  keyHandler,
  LinkTextOSTyped,
  LinkTooltipsContent,
  LinkVisible,
  MenuConfigurationLinksAppearence,
  MenuConfigurationLinksData,
  MenuConfigurationLinksTags,
  MenuConfigurationLinksTooltip,
  MenuDraggable,
  Modale_resolution_png,
  NodeDisplayed,
  NodeTooltipsContent,
  NodeVisibleOnsSvg,
  OpenSankeyConfigurationNodesAttributes,
  OpenSankeyConfigurationsMenus,
  OpenSankeyDefaultModalePreferenceContent,
  OpenSankeyDiagramSelector,
  OpenSankeyMenuConfigurationLayout,
  OpenSankeyMenuConfigurationNodes,
  OpenSankeyMenuConfigurationNodesIO,
  OpenSankeyMenus,
  OpenSankeyToolbarBuilder,
  preferenceCheck,
  RedrawNodesLabel,
  resizeDrawingArea,
  RetrieveExcelResults,
  ReturnValueLink,
  SankeyMenuConfigurationLinks,
  SankeyMenuConfigurationNodesTags,
  SankeyMenuConfigurationNodesTooltip,
  setDiagram,
  ToastWaitFunc,
  updateDrawNodeShape,
  updateLayoutOSTyped,
  ZoomFunction,
} from './import/OpenSankey'
import SankeyDraw from 'open-sankey/dist/draw/SankeyDraw'
import { windowSankey } from 'open-sankey/dist/configmenus/SankeyUtils'
import { SankeySettingsEditionElementTags} from 'open-sankey/dist/configmenus/SankeyMenuConfigurationTags'
import { ModalPreference } from 'open-sankey/dist/dialogs/SankeyMenuPreferences'
import { ContextMenuNode } from 'open-sankey/dist/dialogs/SankeyMenuContextNode'
import { ContextMenuLink } from 'open-sankey/dist/dialogs/SankeyMenuContextLink'
import { ContextMenuZdd } from 'open-sankey/dist/dialogs/SankeyMenuContextZDD'
import { SankeyModalStyleLink, SankeyModalStyleNode } from 'open-sankey/dist/dialogs/SankeyStyle'
import { Menu } from 'open-sankey/dist/topmenus/SankeyMenuTop'
import { SankeySettingsEditionElementTagsTypes } from 'open-sankey/src/configmenus/types/SankeyMenuConfigurationTagsTypes'
import { ContextMenuLinkFType } from 'open-sankey/src/dialogs/types/SankeyMenuContextLinkTypes'
import { ContextMenuNodeFType } from 'open-sankey/src/dialogs/types/SankeyMenuContextNodeTypes'
import { ContextMenuZddFType } from 'open-sankey/src/dialogs/types/SankeyMenuContextZDDTypes'
import { modalPreferenceTypes } from 'open-sankey/src/dialogs/types/SankeyMenuPreferencesTypes'
import { ContextLegendTags } from 'open-sankey/dist/draw/SankeyDrawLegend'
import { setDiagramFuncType } from 'open-sankey/src/configmenus/types/SankeyMenuBannerTypes'
import { SankeyModalStyleLinkFType, SankeyModalStyleNodeFType } from 'open-sankey/src/dialogs/types/SankeyStyleTypes'
import { LinkTooltipsContentFType, NodeTooltipsContentFType } from 'open-sankey/src/draw/types/SankeyTooltipTypes'
import { LinkColorFuncType, LinkTextFuncType } from 'open-sankey/src/configmenus/types/SankeyUtilsTypes'
import { LinkStrokeFuncType } from 'open-sankey/src/draw/types/SankeyDrawLinksTypes'


// Import from OpenSankey+
import { PlusElementsSelectedType, reDrawIllustrationFType, reDrawPlusNodeEventFType, SankeyPlusApplicationDataType, SankeyPlusData, SankeyPlusLabel, ViewType } from 'sankeyanimation/types/Types'
import { SankeyPlusMenuConfigurationFreeLabelsFType } from 'sankeyanimation/types/SankeyPlusMenuConfigurationLabelsTypes'
import { SankeyPlusMenuConfigurationFreeLabels } from 'sankeyanimation/dist/src/SankeyPlusMenuConfigurationLabels'
import {
  LinkStroke, PlusLinkSabotColor, SankeyPlusDrawArrows, PlusDrawLabels,
  PlusDrawNodesFO, SankeyPlusDrawNodesIllustration, PlusNodeClickEvent,
  PlusNodeDragEvent,
  GetDataFromView, sankey_plus_min_width_and_height,
  sankey_plus_zoom_text_zone, SetSvgBg,
  ImportImageAsSvgBg,
  SankeyPlusBannerView, SankeyPlusDiagramSelector, SankeyPlusHyperLink,
  SankeyPlusMenuPreferenceLabels, SankeyPlusMenuPreferenceView,
  SankeyPlusNodeFO, SankeyPlusNodeIcon, SelecteurView,
  apply_transformation_opensankey_plus_elements,
  context_zdt, getSetDiagramFunc, menu_conf_link_apparence_gradient,
  modal_transparent_view_attr, modal_view_not_saved,
  plus_sankey_layout, view_toast, view_toast_update_view,
  viewsAccordion, zdtMenuAsAccordeonItem,
  keyHandler as SankeyViewskeyHandler,
  PlusItemExport

} from './import/SankeyPlus'

// Import from MFASankey
import {
  LinkText,
  AFMNodeTooltipsContent,
  processTrade,
  node_arrow_visible,
  AFMSankeyMenuPreferenceView,
  FreeVariables,
  additional_selector,
  afm_menu_configuration_import_export,
  checkboxFreeNullLink,
  AFMLinkTooltipsContent
} from './import/MFASankey'

// Import from SankeyDev
import { modal_unitary_sankey_sector_nodeFType } from 'sankeydev/types/SankeyDevUnitarySankeyTypes'
import { SankeyDevData, SankeyDevApplicationDataType, reDrawNodeNotToScaleFType } from 'sankeydev/types/Types'
import { modal_unitary_sankey_sector_node } from 'sankeydev/dist/src/SankeyDevUnitarySankey'
import { sankeyDevDialogs } from './import/SankeyDevDialogs'
import {
  MenuConfigHideLoneNode,
  MenuEnregistrerExcelSimple,
  PublishModal,
  SankeyDevDrawNodesNotToScale,
  SankeyDevMenuDataPercent,
  SankeyDevMenuPublishItem,
  SankeyDevNodesAttributes,
  SankeyDev_legend_elements, SankeyDev_link_text,
  context_node_view_node_unitary,
  devGetLinkValue, hiddeLoneNodes, modal_selection_icons, searchAndRestoreLoneNodes
} from './import/SankeyDev'

// - Import from AFMSankey
import { AFMSankeyData } from 'afmsankey/types/Types'
import { SupplyUseModelisationProd } from 'afmsankey/dist/src/lib/SankeyReconciliation'
import {SplitVerticalModal} from 'afmsankey/dist/src/lib/AFMSankeyMenu'

import { welcomeModalBuilder, CardsTemplateBuilder } from './SankeyModalWelcome'
import { updateLayoutFuncType } from 'open-sankey/src/draw/types/SankeyDrawLayoutTypes'

/*******************************************************************************/

const OpenSankeyDraw = SankeyDraw as FunctionComponent<SankeyDrawTypes>
const OpenSankeyMenu : FunctionComponent<MenuTypes> = Menu
const OpenSankeyModalPreference : FunctionComponent<modalPreferenceTypes> = ModalPreference
const OpenSankeySettingsEditionElementTags :
  FunctionComponent<SankeySettingsEditionElementTagsTypes> = SankeySettingsEditionElementTags
const OpenSankeyContextMenuNode : FunctionComponent<ContextMenuNodeFType> = ContextMenuNode
const OpenSankeyContextMenuLink : FunctionComponent<ContextMenuLinkFType> = ContextMenuLink
const OpenSankeyContextMenuZDD : FunctionComponent<ContextMenuZddFType> = ContextMenuZdd
const SankeyDevModalUnitarySector :
  FunctionComponent<modal_unitary_sankey_sector_nodeFType> = modal_unitary_sankey_sector_node
const PlusMenuConfigurationFreeLabels :
  FunctionComponent<SankeyPlusMenuConfigurationFreeLabelsFType> =
    SankeyPlusMenuConfigurationFreeLabels

declare const window: Window &
  typeof globalThis & {
    SankeyToolsStatic:boolean,
    sankey: {
      filiere?: string,
      header?: string,
      footer?: boolean,
      // logo_width?: number,
      // excel?: string,
      // publish?: boolean
      introduction?:boolean,
      toolbar?: boolean
      logo?: string
    }
  }

export const SankeyApp = ({
  data,set_data,
  master_data,set_master_data,
  view,set_view,
  Reinitialization,
  exemple_menu,
  formations_menu,
  has_sankey_suite,
  has_open_sankey_plus,
  has_sankeydev,
  get_default_data,
  suite_convert_data,
  applicationContext,
  dict_variable_elements_selected,
  d_setter_input_value,
  contextMenu
} : SankeyAppTypes) => {
  // ==================
  //  CREATION VARIABLE
  // ==================
  // ----------HOOK---------------
  const SuiteGetLinkValue=(has_sankeydev)?devGetLinkValue:GetLinkValue
  const never_see_again = useRef((localStorage.getItem('dontSeeAggainWelcome')==='1'))
  const active_page = useRef<Dispatch<SetStateAction<string>>>(()=>null)
  const elementToDispose = useRef([''])
  // For afm_menu_configuration_import_export
  //- AFMSankey
  const action_type = useRef('')
  const ref_alt_key_pressed = useRef(false)

  let SuiteNodeTooltipContent=NodeTooltipsContent
  let SuiteLinkTooltipContent=LinkTooltipsContent
  if(has_sankey_suite){
    SuiteNodeTooltipContent=AFMNodeTooltipsContent as unknown as NodeTooltipsContentFType
    SuiteLinkTooltipContent=AFMLinkTooltipsContent as unknown as LinkTooltipsContentFType
  }

  //- OpenSankey+
  const [view_not_saved,pre_set_view_not_saved]=useState('')
  const [view_selected, set_view_selected] = useState('none')
  const animating = useRef(false)
  const r_editor_ZDT= useRef<ReactQuill>() as {current:ReactQuill}
  const accept_simple_click=useRef(true)
  const start_point=useRef([0,0])
  const legend_clicked = useRef(false)

  const set_view_not_saved=(s:string)=>{
    pre_set_view_not_saved(s)
  }

  // Reset list of setter of input link
  dict_variable_elements_selected.displayedInputLinkValueSetterRef.current=[]
  dict_variable_elements_selected.displayedInputLinkDataTagSetterRef.current=[]
  dict_variable_elements_selected.ref_display_link_opacity.current=[]

  // ----------NORMAL VAR---------------
  const display_nodes = Object.keys(data.nodes)
    .filter((key) => NodeDisplayed(data,data.nodes[key]))
    .reduce((obj, key) => {
      return Object.assign(obj, {
        [key]: data.nodes[key]
      })
    }, {}) as {[idNode:string]:SuiteSankeyNode}
  const pre_display_links=Object.keys(data.links)
    .filter((key) =>LinkVisible(data.links[key],data,display_nodes))
    .reduce((obj, key) => {
      return Object.assign(obj, {
        [key]: data.links[key]
      })
    }, {}) as {[idLink:string]:SuiteSankeyLink}
  const pre_link_key=Object.keys(pre_display_links)
  const display_links={} as {[idLink:string]:SuiteSankeyLink}
  data.linkZIndex.filter(lk=>pre_link_key.includes(lk))
    .forEach(lk=>display_links[lk]=pre_display_links[lk])

  const recomputeDisplayedElement=()=>{
    dict_variable_application_data.display_nodes = Object.keys(data.nodes)
      .filter((key) => NodeDisplayed(data,data.nodes[key]))
      .reduce((obj, key) => {
        return Object.assign(obj, {
          [key]: data.nodes[key]
        })
      }, {}) as {[idNode:string]:SuiteSankeyNode}

    const pre_display_links=Object.keys(data.links)
      .filter((key) =>LinkVisible(data.links[key],data,dict_variable_application_data.display_nodes))
      .reduce((obj, key) => {
        return Object.assign(obj, {
          [key]: data.links[key]
        })
      }, {}) as {[idLink:string]:SuiteSankeyLink}
    const pre_link_key=Object.keys(pre_display_links)

    dict_variable_application_data.display_links={}
    data.linkZIndex=pre_link_key
    pre_link_key.forEach(lid=>dict_variable_application_data.display_links[lid]=data.links[lid])

    // delete element no longer displayed
    const curr_displayed_nodes= Object.keys(dict_variable_application_data.display_nodes)
    const node_to_delete=NodeVisibleOnsSvg().filter(nid=>!curr_displayed_nodes.includes(nid))
    DeleteGNodes(node_to_delete)


    applyZoomEvent(dict_variable_application_data,size_of_draw_zone)

  }


  if(has_sankeydev){
  // hiddeLoneNodes => Function that search and hide node that have 1 input link, 1 output link and have hideLoneNode at true
  // Example ('-->' represent a link):
  // node0-->node1-->node2
  // If we click on 'hide lone node' in the configuration menu  while node1 is selected then we get :
  // node0-->node2
    hiddeLoneNodes( {data:data,display_nodes:display_nodes,display_links:display_links,set_data:set_data} as unknown as SankeyDevApplicationDataType)
  }
  // If we have the sankeydev token then 'restore' all node who had previously 'hideLoneNode' at true
  // but at this render it's false
  // Else if we don't have sasnkeydev token we restore all node event if 'hideLoneNode' is true
  searchAndRestoreLoneNodes(data,set_data,has_sankeydev)

  const position = 'relative'

  const mode_pref=sessionStorage.getItem('modepref')
  if(mode_pref && mode_pref==='expert' && data.accordeonToShow.length!==6){
    data.accordeonToShow = ['MEP', 'EN', 'EF', 'ED', 'LL', 'Vis']
  }
  // Reset array of setter of wysiwyg object (edt)
  d_setter_input_value.r_setter_editor_content_fo_zdt.current=[]

  // ================================
  //  CREATION GENERAL FUNCTION
  // ================================

  const size_of_draw_zone=(data.labels && Object.values(data.labels).length>0)?
    sankey_plus_min_width_and_height:GetSankeyMinWidthAndHeight
  const default_node_arrow_visible =
  (data:SuiteSankeyData,n: SuiteSankeyNode) => !NodeDisplayed(data,n) || (n.inputLinksId.length === 0) || (!ReturnValueLink(data,data.links[n.inputLinksId[0]],'arrow')) ? false : true

  // Test for link text function if we have a token and if so we complet a certain condition
  const pre_link_text=(has_sankey_suite)?LinkText:LinkTextOSTyped
  // Test if the current data is a vue of a unitary node so we display link value as percent
  const SuiteSankeyLinkText=data.unitary_node.length>0?SankeyDev_link_text:pre_link_text

  // Function to close all menu : menu confugartion, menu context (nodes,links, drawZone), an menu dragggable
  // Called when we press escape
  const closeAllMenu=()=>{
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_menu_node_apparence.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_menu_node_io.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_menu_node_tooltip.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_menu_link_data.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_menu_link_appearence.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_menu_link_tooltip.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_menu_layout.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_apply_layout.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_menu_zdt.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_menu_node_icon.current!(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_menu_node_tags.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_menu_link_tags.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_resolution_save_png.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_style_node.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_style_link.current(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_modal_preference.current!(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_modal_welcome.current!(false)
    dict_hook_ref_setter_show_dialog_components.ref_setter_show_modale_support.current(false)

    contextMenu.closeAllMenuContext()
  }
  const SuiteNodeArrowVisible =       has_sankey_suite ? node_arrow_visible as(data:SankeyData,n: SankeyNode)=>boolean
    :default_node_arrow_visible as(data:SankeyData,n: SankeyNode)=>boolean

  const suiteDrawArrows= SankeyPlusDrawArrows
  const suiteLinkStroke= LinkStroke

  // Color for the sabot when the source node is an arrow

  const SuiteRedrawLinks=(links_to_update:SankeyLink[])=>{
    drawLinkShape(dict_variable_application_data,dict_variable_elements_selected,applicationContext,link_function,links_to_update,ComponentUpdater)
    AddDrawLinksEvent(contextMenu,dict_variable_application_data,uiElementsRef,dict_variable_elements_selected,link_function,ComponentUpdater,applicationContext,ref_alt_key_pressed)
    ComponentUpdater.updateComponenSaveInCache.current(false)

  }

  const SuiteCreateLinksOnSVG=(links_to_update:SankeyLink[])=>{
    drawAddLinks(
      contextMenu,
      dict_variable_application_data,
      uiElementsRef,
      dict_variable_elements_selected,
      applicationContext,
      ref_alt_key_pressed,
      link_function,
      ComponentUpdater,
      dict_hook_ref_setter_show_dialog_components,
      links_to_update
    )
    ComponentUpdater.updateComponenSaveInCache.current(false)
  }

  const SuiteReDrawLinkStartSabot:SuiteRedrawNodesFType=(nodes_to_update)=>{

    const scale = d3.scaleLinear()
      .domain([0, data.user_scale])
      .range([0, 100])
    const inv_scale = d3.scaleLinear()
      .domain([0, 100])
      .range([0, data.user_scale])
    nodes_to_update.forEach(n=>{
      DrawLinkStartSabot(dict_variable_application_data,n,scale,inv_scale,GetLinkValue,PlusLinkSabotColor as LinkColorFuncType)
    })
  }
  
  const link_function:SuiteLinkFunctionTypes = {
    GetLinkValue:SuiteGetLinkValue,
    LinkText:(SuiteSankeyLinkText as LinkTextFuncType),
    DrawArrows:suiteDrawArrows,
    LinkStroke:(suiteLinkStroke as LinkStrokeFuncType),
    LinkSabotColor:(PlusLinkSabotColor as LinkColorFuncType),
    reDrawLinkStartSabot:SuiteReDrawLinkStartSabot ,
    node_arrow_visible:SuiteNodeArrowVisible,
    LinkTooltipsContent:SuiteLinkTooltipContent,
    DrawAllLinks,
    drawAddLinks,
    drawLinkShape,
    RedrawLinks:SuiteRedrawLinks,
    CreateLinksOnSVG:SuiteCreateLinksOnSVG as CreateLinksOnSVGFType

  }

  const suiteAdjustSankey=()=>{
    AdjustSankeyZone(dict_variable_application_data,size_of_draw_zone)
    DrawGrid(data)
  }
  const suiteResizeCanvas=()=>{
    resizeDrawingArea(dict_variable_application_data,size_of_draw_zone)
    DrawGrid(data)
  }
  const reDrawLegend=()=>{
    DrawLegend(dict_variable_application_data,applicationContext,contextMenu,GetLinkValue,legend_clicked,ComponentUpdater,reDrawLegend,suiteResizeCanvas)
    ComponentUpdater.updateComponenSaveInCache.current(false)
  }

  const reDrawIllustration=(nodes_to_update:SuiteSankeyNode[])=>{
    SankeyPlusDrawNodesIllustration(
      data,
      nodes_to_update,
      dict_variable_elements_selected,
      SuiteNodeTooltipContent,
      SuiteGetLinkValue,t
    )
  }
  const reDrawPlusNodeEvent=(nodes_to_update:SuiteSankeyNode[])=>{
    PlusNodeClickEvent(
      dict_variable_application_data as SankeyPlusApplicationDataType,
      dict_variable_elements_selected,
      uiElementsRef,
      animating,
      accept_simple_click,
      SuiteGetLinkValue,
      ComponentUpdater,
      nodes_to_update
    )
  }
  const reDrawNodeNotToScale=(nodes_to_update:SuiteSankeyNode[])=>{
    SankeyDevDrawNodesNotToScale(dict_variable_application_data as SankeyDevApplicationDataType,nodes_to_update)
  }

  const SuiteRedrawNode=(nodes_to_update:SuiteSankeyNode[])=>{
    updateDrawNodeShape(dict_variable_application_data,link_function,dict_variable_elements_selected.multi_selected_nodes,nodes_to_update)
    RedrawNodesLabel(dict_variable_application_data,nodes_to_update,GetLinkValue,applicationContext.t,node_function)
    reDrawIllustration(nodes_to_update)

    reDrawPlusNodeEvent(nodes_to_update)

    PlusNodeDragEvent(
    dict_variable_application_data as SankeyPlusApplicationDataType,
    dict_variable_elements_selected as PlusElementsSelectedType,
    applicationContext,
    ref_alt_key_pressed.current,
    SuiteSankeyLinkText as LinkTextFuncType,
    SuiteGetLinkValue,
    size_of_draw_zone,ComponentUpdater,
    node_function,
    link_function,
    applicationDraw
    )

    PlusDrawNodesFO(
      data,
      display_nodes,
      dict_variable_elements_selected,
      SuiteNodeTooltipContent,
      SuiteGetLinkValue,
      t
    )
    reDrawNodeNotToScale(nodes_to_update)
    ComponentUpdater.updateComponenSaveInCache.current(false)
  }
  const reDrawPlusLabels=(object_to_update:SankeyPlusLabel[])=>{
    PlusDrawLabels(
      dict_variable_application_data as SankeyPlusApplicationDataType,
      dict_variable_elements_selected as PlusElementsSelectedType,
      uiElementsRef,
      contextMenu,
      applicationContext,
      d_setter_input_value,
      size_of_draw_zone,
      start_point,
      contextMenu.closeAllMenuContext,
      ComponentUpdater,
      object_to_update,
      reDrawPlusLabels,
      link_function,
      applicationDraw
    )
    ComponentUpdater.updateComponenSaveInCache.current(false)

  }

  const SuiteCreateNodeOnSVG=(nodes_to_update:SuiteSankeyNode[])=>{
    drawAddNodes(contextMenu,
      dict_variable_application_data,
      uiElementsRef,
      dict_variable_elements_selected,
      applicationContext,
      ref_alt_key_pressed,accept_simple_click,
      link_function,
      SuiteNodeTooltipContent,
      ComponentUpdater,
      dict_hook_ref_setter_show_dialog_components,
      node_function,nodes_to_update,
      size_of_draw_zone,
      applicationDraw
    )

    PlusNodeDragEvent(
      dict_variable_application_data as SankeyPlusApplicationDataType,
      dict_variable_elements_selected as PlusElementsSelectedType,
      applicationContext,
      ref_alt_key_pressed.current,
      SuiteSankeyLinkText as LinkTextFuncType,
      SuiteGetLinkValue,
      size_of_draw_zone,ComponentUpdater,
      node_function,
      link_function,
      applicationDraw
    )

    PlusDrawNodesFO(
      data,
      display_nodes,
      dict_variable_elements_selected,
      SuiteNodeTooltipContent,
      SuiteGetLinkValue,
      t
    )
    reDrawNodeNotToScale(nodes_to_update)
    ComponentUpdater.updateComponenSaveInCache.current(false)
  }


  const node_function:SuiteNodesFunctionTypes={
    DrawAllNodes,
    drawAddNodes,
    RedrawNodes:SuiteRedrawNode as RedrawNodesFType,
    recomputeDisplayedElement,
    reDrawIllustration:reDrawIllustration as reDrawIllustrationFType,
    reDrawPlusNodeEvent:reDrawPlusNodeEvent as reDrawPlusNodeEventFType,
    reDrawNodeNotToScale : reDrawNodeNotToScale as reDrawNodeNotToScaleFType,
    CreateNodesOnSVG:SuiteCreateNodeOnSVG as drawNodesFType
  }

  // Function used to update some variables(mode) of dict_variable_application_data.data with from another dict_variable_application_data.data(new_layout)
  // We create a function combining the update_layout function of 2 modules  : OpenSankey & OpenSankey+
  const SuiteUpdateLayout=(
    data: SuiteSankeyData,
    new_layout: SuiteSankeyData,
    mode:string[],
    synchronize:boolean
  )=>{
    updateLayoutOSTyped(data,new_layout,mode,synchronize)
    plus_sankey_layout(data,new_layout,mode)

    if(mode.includes('Views') && dict_variable_application_data.view!=='none'){
      plus_sankey_layout(dict_variable_application_data.master_data as SankeyPlusData,new_layout,['Views'])
    }
  }

  // ================================
  //  CREATION DICT OF VARIABLE USED
  // ================================

  /***********************************************************************************************/
  const dict_variable_application_data : suiteApplicationDataType = {
    data,
    set_data ,
    get_default_data,
    master_data,
    set_master_data,
    display_nodes,
    display_links,
    view,
    set_view,
    function_on_wait : useRef(()=>null),
  }
  /*******************************************************************************/
  const uiElementsRef : SuiteUiElementsRefType = {
    button_ref : useRef<HTMLLabelElement>(null),
    accordion_ref : useRef<HTMLDivElement>(null),
    links_accordion_ref : useRef<HTMLDivElement>(null),
    zdt_accordion_ref : useRef<HTMLDivElement>(null),
    nodes_accordion_ref : useRef<HTMLDivElement>(null),
    ref_setter_nav_item_active : useRef<Dispatch<SetStateAction<number>>>(()=>null),
    ref_nav_item_active : useRef<number>(-1),
    ref_setter_sub_nav_item_active : useRef<Dispatch<SetStateAction<string>>>(()=>null),
  }

  /*******************************************************************************/
  const agregation : agregationType = {
    showAgregationRef : useRef<[boolean, Dispatch<SetStateAction<boolean>>][]>([]),
    isAgregationRef : useRef<boolean>(true),
    agregationNode : useRef<SankeyNode>()
  }
  /*******************************************************************************/
  const dict_hook_ref_setter_show_dialog_components : SankeySuiteShowMenuComponentsType = {
    // OpenSankey
    ref_setter_show_menu_node_apparence : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_menu_node_io : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_menu_node_tooltip : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_menu_node_tags : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_menu_link_tags : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_menu_link_data : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_menu_link_appearence : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_menu_link_tooltip : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_menu_layout : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_modal_welcome : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_modale_tuto : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_modale_support : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_excel_dialog : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_save_json : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_getter_show_save_json : useRef(false),
    ref_setter_show_apply_layout : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_modal_preference : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_modal_template : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_load : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_style_node : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_style_link : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_waiting : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),

    // - OpenSankey+
    ref_setter_show_menu_node_icon : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_menu_zdt : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    show_toast_new_view  :  useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    show_toast_update_view  :  useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_is_for_unitary_view:  useRef<Dispatch<SetStateAction<boolean>>>(()=>null),

    ref_setter_show_modal_import_icons : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_modal_transparent_view_attr :
      useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    // - SankeyDev
    ref_setter_show_publish_dialog : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_resolution_save_png : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_modal_edit_link_reference_unitary :
      useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_unitary_view_modal : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    // - AFMSankey
    ref_setter_show_split_trade_dialog : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    ref_setter_show_reconciliation : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    // - SankeySuite
    ref_setter_show_toast_limit_node  :  useRef<Dispatch<SetStateAction<boolean>>>(()=>null)
  }

  const ComponentUpdater:SuiteComponentUpdaterType={
    updateComponentMenuConfigNode:useRef(()=>null),
    updateComponentMenuConfigNodeAppearence:useRef(()=>null),
    updateComponentMenuConfigLink:useRef(()=>null),
    updateComponentToolbar:useRef(()=>null),
    updateComponentMenuConfig:useRef(()=>null),
    updateComponentMenuConfigLayout:useRef(()=>null),
    updateComponentMenu:useRef(()=>null),
    updateComponentMenuConfigZdt:useRef([] as (()=>void)[]),
    updateComponenSaveInCache:useRef(()=>null),
    updateComponentMenuNodeIOSelectSideNode:useRef([] as (()=>void)[])
  }
  ComponentUpdater.updateComponentMenuNodeIOSelectSideNode.current=[]
  ComponentUpdater.updateComponentMenuConfigZdt.current=[]
  /*******************************************************************************/
  const processFunctions : processFunctionsType = {
    ref_processing : useRef(false),
    ref_setter_processing : useRef<Dispatch<SetStateAction<boolean>>>(()=>null),
    failure : useRef(false),
    not_started : useRef(true),
    ref_result : useRef<Dispatch<SetStateAction<string>>>(()=>null),
    path : useRef(''),
    launch : (path:string) => {
      processFunctions.path.current = path
      dict_hook_ref_setter_show_dialog_components.ref_setter_show_load.current!(true)
      processFunctions.ref_setter_processing.current(true)
      processFunctions.failure.current = true
      processFunctions.not_started.current = false
      processFunctions.ref_result.current('')
    },
    RetrieveExcelResults
  }
  /*******************************************************************************/
  const applicationDraw : applicationDrawType = {
    GetSankeyMinWidthAndHeight : size_of_draw_zone,
    updateLayout : SuiteUpdateLayout as updateLayoutFuncType,
    resizeCanvas:suiteResizeCanvas,
    reAdjustSankey:suiteAdjustSankey
  }

  if(has_open_sankey_plus && !data.view){
    data.view=[]
    set_data({...data})
  }
  /*******************************************************************************/

  // Convertie data pour prendre mettre a jour un data qui ne contiendrais pas certains paramètre verouiller derrière des modules
  if (data.view && data.view.length > 0 && !master_data) {
    set_master_data({...JSON.parse(JSON.stringify(data))})
    if(data.current_view && data.current_view!=='none'){
      const view_to_display= GetDataFromView(data,data.current_view)
      set_view(data.current_view)
      // if(d_setter_input_value.r_setter_value_editor_name_view.current){
      //   d_setter_input_value.r_setter_value_editor_name_view
      //     .current!(data.view.filter(v=>v.id===data.current_view)[0].nom)
      // }
      set_data(view_to_display as SuiteSankeyData)
    }
  }

  //- 1. Builds Configuration Menus
  //- 1.1 Builds Configuration Menus Layout
  const OSP_BG_SVG_input=ImportImageAsSvgBg(applicationContext.t,
    dict_variable_application_data.data,
    dict_variable_application_data.set_data as (_:SankeyPlusData)=>void,
    applicationContext.has_open_sankey_plus)

  const menu_configuration_layout = OpenSankeyMenuConfigurationLayout(
    applicationContext,
    dict_variable_application_data,
    dict_variable_elements_selected,
    OSP_BG_SVG_input,
    node_function,
    link_function,
    reDrawLegend,
    ComponentUpdater
  )

  //- 1.2 Builds Configuration Menus Node
  //- 1.2.1 Builds Configuration Menus Node Attributes
  const menu_config_add_node_appearence_attr=[] as JSX.Element[]
  const menu_config_add_node_label=[] as JSX.Element[]
  const menu_config_add_node_label_value=[] as  JSX.Element[]

  const SankeyDevNodeAppearenceAttr=SankeyDevNodesAttributes(
    applicationContext.t,
    dict_variable_application_data.data,
    dict_variable_elements_selected,
    applicationContext.has_sankeydev,
    node_function,
    false
  ) as unknown as JSX.Element

  // Create component to add to the configuration and style menu that manage nodes appaearence attribute
  // Which allow to modify variable used by SankeyDev
  if(applicationContext.has_sankeydev){

    // Attributes to draw nodes with a 'scattered' shape
    menu_config_add_node_appearence_attr.push(
      SankeyDevNodeAppearenceAttr
    )

    // Attributes to hide nodes if their attributes hide_lone_node is at true
    // & they have only 1 output link and 1 inputlink
    menu_config_add_node_appearence_attr.push(
      MenuConfigHideLoneNode(
        applicationContext.t,
        dict_variable_application_data.data,
        dict_variable_application_data.set_data as (_:SankeyDevData)=>void,
        dict_variable_elements_selected,
        false
      )
    )
  }

  const menu_configuration_nodes_attributes = OpenSankeyConfigurationNodesAttributes(
    applicationContext,
    dict_variable_application_data,
    dict_variable_elements_selected,
    false,
    dict_variable_elements_selected.ref_selected_style_node,
    menu_config_add_node_appearence_attr,
    menu_config_add_node_label,
    menu_config_add_node_label_value,
    link_function,
    ComponentUpdater,
    node_function
  )

  //- 1.2.2 Builds Configuration Menus Node
  const menu_configuration_nodes = OpenSankeyMenuConfigurationNodes(
    applicationContext,
    dict_variable_application_data,
    dict_variable_elements_selected,
    contextMenu,
    menu_configuration_nodes_attributes,
    SuiteGetLinkValue,
    node_function,link_function,
    ComponentUpdater
  )

  //- 1.2.2 bis (Add additional tab in the node configuration menu)
  menu_configuration_nodes['icon']=SankeyPlusNodeIcon(
    applicationContext.t,
    dict_variable_application_data.data,
    dict_variable_elements_selected.multi_selected_nodes,
    applicationContext.has_open_sankey_plus,
    false,
    dict_hook_ref_setter_show_dialog_components,
    node_function)

  menu_configuration_nodes['fo']=SankeyPlusNodeFO(
    applicationContext.t,
    dict_variable_application_data.data,
    dict_variable_elements_selected.multi_selected_nodes,
    applicationContext.has_open_sankey_plus,
    d_setter_input_value,node_function)

  menu_configuration_nodes['hl']=SankeyPlusHyperLink(
    applicationContext.t,
    dict_variable_application_data.data,
    dict_variable_elements_selected.multi_selected_nodes,
    applicationContext.has_open_sankey_plus,node_function)

  //- 1.2.3 Builds Configuration Menus Node Tags
  const menu_configuration_nodes_tags=<OpenSankeySettingsEditionElementTags
    applicationContext={applicationContext}
    dict_variable_application_data={dict_variable_application_data}
    elementTagNameProp='nodeTags'
    elementNameProp='nodes'
    node_function={node_function}
    link_function={link_function}
    ComponentUpdater={ComponentUpdater}
    reDrawLegend={reDrawLegend}
  />

  //- 1.3 Builds Configuration Menus Links
  //- 1.3.1.1 Builds different subpart for Menu Links according to activated token
  const additional_data_element=[] as JSX.Element[]
  const add_link_appearence_item=[] as JSX.Element[]


  if(applicationContext.has_sankeydev){
    additional_data_element.push(
      SankeyDevMenuDataPercent(
        dict_variable_application_data.data,
        dict_variable_application_data.set_data as (_:SankeyDevData)=>void,
        dict_variable_elements_selected.multi_selected_links
      )
    )
  }

  add_link_appearence_item.push(
    menu_conf_link_apparence_gradient(
      applicationContext,
      ComponentUpdater,
      dict_variable_elements_selected.multi_selected_links,
      dict_variable_application_data.data,
      link_function,
      applicationContext.has_open_sankey_plus,
      false,
      dict_variable_elements_selected.ref_selected_style_link))

  if (applicationContext.has_sankey_suite) {
    add_link_appearence_item.push(
      FreeVariables(
        applicationContext.t,
        dict_variable_application_data,
        dict_variable_elements_selected,
        node_function,link_function
      )
    )
  }

  //- 1.3.1.2 Builds the core for Menu Links
  const menu_configuration_links = SankeyMenuConfigurationLinks(
    dict_variable_application_data,
    dict_variable_elements_selected,
    applicationContext,
    additional_data_element,
    add_link_appearence_item,
    link_function,
    ComponentUpdater,
    node_function)

  //- 1.3.2 Builds Configuration Menus Link tags
  const  menu_configuration_link_tags=<OpenSankeySettingsEditionElementTags
    applicationContext={applicationContext}
    dict_variable_application_data={dict_variable_application_data}
    elementTagNameProp='fluxTags'
    elementNameProp='links'
    node_function={node_function}
    link_function={link_function}
    ComponentUpdater={ComponentUpdater}
    reDrawLegend={reDrawLegend}
  />

  //- 1.4 Builds Configuration Menus DataTags
  const  menu_configuration_data_tags=<OpenSankeySettingsEditionElementTags
    applicationContext={applicationContext}
    dict_variable_application_data={dict_variable_application_data}
    elementTagNameProp='dataTags'
    elementNameProp='links'
    node_function={node_function}
    link_function={link_function}
    ComponentUpdater={ComponentUpdater}
    reDrawLegend={reDrawLegend}
  />

  //- 1.5 Builds Configuration Menus FreeLabel
  let menu_configuration_free_labels=<></>
  const modifcation_zdt_component = <PlusMenuConfigurationFreeLabels
    data={dict_variable_application_data.data}
    multi_selected_label={dict_variable_elements_selected.multi_selected_label}
    t={applicationContext.t}
    is_activated={applicationContext.has_open_sankey_plus}
    d_setter_input_value={d_setter_input_value}
    r_editor_ZDT={r_editor_ZDT}
    reDrawPlusLabels={reDrawPlusLabels}
    ComponentUpdater={ComponentUpdater}

  />
  menu_configuration_free_labels=  zdtMenuAsAccordeonItem(dict_variable_application_data.data,
    uiElementsRef,
    applicationContext,
    modifcation_zdt_component)


  //- 1.7 Finish builds Configuration Menus
  const configurations_menus = OpenSankeyConfigurationsMenus(
    dict_variable_application_data,
    dict_variable_elements_selected,
    applicationContext,
    uiElementsRef,
    dict_hook_ref_setter_show_dialog_components,
    menu_configuration_layout,
    menu_configuration_nodes_tags,
    menu_configuration_link_tags,
    menu_configuration_data_tags,
    menu_configuration_nodes,
    menu_configuration_links,
    [menu_configuration_free_labels],
    applicationContext.token,
    link_function,
    ComponentUpdater,
    contextMenu,
    ref_alt_key_pressed,
    node_function
  )

  const _load_json = useRef<HTMLInputElement>(null) as { current: HTMLInputElement; }
  const _load_json_catalog = useRef<HTMLInputElement>(null) as { current: HTMLInputElement; }

  // Create the view selector used in different component (Subnav 'View' & 'Unitary view' in the top navbar
  // and in the View accordeopn item)
  const selector_of_view=SelecteurView(
    dict_variable_application_data as SankeyPlusApplicationDataType,
    dict_variable_elements_selected,
    applicationContext.t,
    set_view_not_saved,
    applicationContext.has_open_sankey_plus,
    d_setter_input_value)


  configurations_menus.splice(4,0,
    viewsAccordion(
      dict_variable_application_data as SankeyPlusApplicationDataType,
      uiElementsRef,
      _load_json,
      applicationContext.t,
      applicationContext.has_open_sankey_plus,
      suite_convert_data,
      dict_variable_application_data.get_default_data,
      selector_of_view
    ))


  //- construct here as hook Inside afm_menu_configuration_import_export can not be called Inside if.
  const the_afm_menu_configuration_import_export = afm_menu_configuration_import_export(
    dict_variable_application_data,
    uiElementsRef,applicationContext.t,
    node_function,link_function
  )
  if (applicationContext.has_sankey_suite) {
    configurations_menus.splice(3,0,the_afm_menu_configuration_import_export)
  }

  //- End of 1. Build configuration Menus

  //- 2.  Build Menus
  //- 2.1 Build Menus dropdown
  //- 2.2 Build Menus banner
  //- 2.3 Build Menus toolbar
  const additional_edition_item=[] as JSX.Element[]
  const additional_file_save_item=[] as JSX.Element[]
  const additional_file_item=[] as JSX.Element[]
  const additional_file_export_item=[] as JSX.Element[]

  if(applicationContext.has_sankeydev){
    const item_publish = SankeyDevMenuPublishItem(
      applicationContext.t,
      dict_hook_ref_setter_show_dialog_components
    )
    additional_file_item.push(item_publish)
  }

  const modal_resolution_png=Modale_resolution_png(applicationContext.t,
    dict_hook_ref_setter_show_dialog_components,dict_variable_application_data,contextMenu.pointer_pos
  )

  if(applicationContext.has_sankeydev){
    const item_save_excel_simple=MenuEnregistrerExcelSimple(
      applicationContext.url_prefix,
      dict_variable_application_data.data
    )
    additional_file_save_item.push(item_save_excel_simple)
  }

  if(applicationContext.has_open_sankey_plus){
    additional_file_export_item.push(PlusItemExport())
  }

  const useOpenSankeySetDiagram = true

  const curSetDiagram : setDiagramFuncType = useOpenSankeySetDiagram ? getSetDiagramFunc(
    dict_variable_application_data.set_master_data as (_:SankeyPlusData|undefined)=>void,
    dict_variable_application_data.set_view,
    dict_variable_application_data.get_default_data
  ) : setDiagram

  const sankey_menus = OpenSankeyMenus(
    applicationContext.t,
    Reinitialization,
    dict_variable_application_data.get_default_data,
    dict_hook_ref_setter_show_dialog_components,
    never_see_again,
    dict_variable_application_data.data,
    dict_variable_application_data.set_data as (d:SankeyData)=>void,
    additional_edition_item,
    additional_file_item,
    additional_file_export_item,
    additional_file_save_item,
    suite_convert_data,
    curSetDiagram
  )

  // 2.4 Modal linked to menu item
  const external_menu_modal=[] as JSX.Element[]
  const regular_ui=OpenSankeyDefaultModalePreferenceContent(applicationContext.t,
    dict_variable_application_data.data,i18next,ComponentUpdater)

  const add_style_link_appearence_item=[] as JSX.Element[]
  add_style_link_appearence_item.push(menu_conf_link_apparence_gradient(
    applicationContext,
    ComponentUpdater,
    {current:[]},dict_variable_application_data.data,
    link_function,
    applicationContext.has_open_sankey_plus, true,
    dict_variable_elements_selected.ref_selected_style_link
  ))

  const modale_style_link=<React.Fragment key={'modale_style_link'}>{(SankeyModalStyleLink as SankeyModalStyleLinkFType)(
    applicationContext,
    dict_variable_application_data,
    dict_variable_elements_selected,
    dict_hook_ref_setter_show_dialog_components,
    contextMenu.pointer_pos,
    add_style_link_appearence_item,
    link_function,ComponentUpdater
  )
  }</React.Fragment>


  const menu_style_add_node_appearence_attr=[] as JSX.Element[]
  const menu_style_add_node_label=[] as JSX.Element[]
  const menu_style_add_node_label_value=[] as  JSX.Element[]

  const SankeyDevNodeAppearenceAttrStyle=      SankeyDevNodesAttributes(
    applicationContext.t,
    dict_variable_application_data.data,
    dict_variable_elements_selected,
    applicationContext.has_sankeydev,
    node_function,
    true
  ) as unknown as JSX.Element
  if(applicationContext.has_sankeydev){
    // Attributes to draw nodes with a 'scattered' shape
    menu_style_add_node_appearence_attr.push(
      SankeyDevNodeAppearenceAttrStyle
    )

    // Attributes to hide nodes if their attributes hide_lone_node is at true
    // & they have only 1 output link and 1 inputlink
    menu_style_add_node_appearence_attr.push(
      MenuConfigHideLoneNode(
        applicationContext.t,
        dict_variable_application_data.data,
        dict_variable_application_data.set_data as (_:SankeyDevData)=>void,
        dict_variable_elements_selected,
        true
      )
    )
  }
  // Create a second component to modify node appearence attributes
  // not for selected nodes this time but for style
  const menu_style_nodes_attributes = OpenSankeyConfigurationNodesAttributes(
    applicationContext,
    dict_variable_application_data,
    dict_variable_elements_selected,
    true,
    dict_variable_elements_selected.ref_selected_style_node,
    menu_style_add_node_appearence_attr,
    menu_style_add_node_label,
    menu_style_add_node_label_value,
    link_function,
    ComponentUpdater,
    node_function
  )

  const modale_style_node=<React.Fragment key={'modale_style_node'}>{(SankeyModalStyleNode as SankeyModalStyleNodeFType)(
    applicationContext,
    dict_variable_application_data,
    dict_hook_ref_setter_show_dialog_components,
    dict_variable_elements_selected.ref_selected_style_node,
    ComponentUpdater,node_function,contextMenu.pointer_pos,
    menu_style_nodes_attributes
  )}</React.Fragment>

  // Warning when we try to create node with mouse click and we have exceeded the number for unconnected user

  // Add Checkbox in preference menu to display the storytelling menu in menu configuration

  regular_ui['form'].push(AFMSankeyMenuPreferenceView(
    applicationContext.t,
        dict_variable_application_data.data as AFMSankeyData,
        preferenceCheck,ComponentUpdater)
  )
  regular_ui['form'].push(SankeyPlusMenuPreferenceView(applicationContext.t,
    dict_variable_application_data.master_data?
      dict_variable_application_data.master_data:
      dict_variable_application_data.data,
    dict_variable_application_data.set_data as (_:SankeyPlusData)=>void,
    preferenceCheck))

  regular_ui['form'].push(SankeyPlusMenuPreferenceLabels(applicationContext.t,
    dict_variable_application_data.data,
    ComponentUpdater))

  const elments_of_modale_preference=Object.values(regular_ui).map(d=>{
    return <>{d}<hr style={{ borderStyle: 'none', margin: '10px', color: 'grey', backgroundColor: 'grey', height: 1 }} /></>
  })

  const modale_preference=<React.Fragment key={'modale_preference'}><OpenSankeyModalPreference
    dict_hook_ref_setter_show_dialog_components={dict_hook_ref_setter_show_dialog_components}
    ui={elments_of_modale_preference}
    t={applicationContext.t}
    pointer_pos={contextMenu.pointer_pos}
  /></React.Fragment>

  const modalViewNotSaved=modal_view_not_saved(
    view_not_saved, set_view_not_saved,
    applicationContext.t,
    dict_variable_application_data
  )

  const modalAttrTransparentView=modal_transparent_view_attr(
    dict_hook_ref_setter_show_dialog_components,
    dict_variable_application_data,
    dict_variable_application_data.master_data?.view
      .filter(v=>v.id===dict_variable_application_data.master_data!.current_view)[0]??{} as
        ViewType,
    applicationContext.t
  )
  external_menu_modal.push(modalAttrTransparentView)

  const intro_modal = welcomeModalBuilder(
    applicationContext,
    dict_variable_application_data,
    exemple_menu,
    dict_hook_ref_setter_show_dialog_components,
    never_see_again,
    active_page
  )
  const cardsTemplate = CardsTemplateBuilder(
    exemple_menu,
    dict_variable_elements_selected,
    dict_variable_application_data,
    applicationContext
  )

  external_menu_modal.push(modale_style_link)
  external_menu_modal.push(modale_style_node)
  external_menu_modal.push(modale_preference)
  external_menu_modal.push(toast_warning_limit_node(dict_hook_ref_setter_show_dialog_components))
  if (!window.SankeyToolsStatic || window.sankey.introduction) {
    external_menu_modal.push(intro_modal)
  }
  external_menu_modal.push(modalViewNotSaved)

  if(applicationContext.has_sankeydev){
    const publishImpl = (file_path:string) =>{
      const path = windowSankey.location.href
      const url = path + '/sankeydev/sankey/publish'
      const new_data = JSON.parse(JSON.stringify((dict_variable_application_data.master_data && dict_variable_application_data.view!=='none')?dict_variable_application_data.master_data:dict_variable_application_data.data))

      new_data.file_name = file_path

      const fetchData = {
        method: 'POST',
        body: JSON.stringify(new_data, null, 2)
      }

      fetch(url, fetchData).then( response => {
        if (!response.ok) {
          alert(response)
        }
      })
      dict_hook_ref_setter_show_dialog_components.ref_setter_show_publish_dialog.current!(false)
    }

    const modale_publish=<PublishModal
      key={'publish'}
      t={applicationContext.t}
      dict_hook_ref_setter_show_dialog_components={dict_hook_ref_setter_show_dialog_components}
      publishImpl = {publishImpl}
      data = {dict_variable_application_data.data} />

    external_menu_modal.push(modale_publish)
  }

  const func_current_filter=(
    new_current_filter: number
  ) => {
    const { display_style } = dict_variable_application_data.data
    display_style.filter = +new_current_filter
    SuiteRedrawNode(Object.values(dict_variable_application_data.display_nodes)as SuiteSankeyNode[])
    SuiteRedrawLinks(Object.values(dict_variable_application_data.display_links))
  }

  const opacity_advanced =  '0'
  //Popover element to handle node levels (aggregation)
  if ('Primaire' in dict_variable_application_data.data.levelTags && dict_variable_application_data.data.levelTags['Primaire'].activated === true) {
    Object.values(dict_variable_application_data.data.levelTags).forEach(tag_group=> {
      if (tag_group.siblings && tag_group.siblings.length > 0) {
        return
      }
      tag_group.activated = true
    })
    Object.values(dict_variable_application_data.data.levelTags).forEach(tag_group=> {
      if (tag_group.siblings && tag_group.siblings.length > 0 && tag_group.activated ) {
        tag_group.siblings.forEach(sibling=>{
          dict_variable_application_data.data.levelTags[sibling].activated=false
        })
      }
    })
    if (Object.values(dict_variable_application_data.data.levelTags).length > 1) {
      dict_variable_application_data.data.levelTags['Primaire'].activated = false
    }
  }
  const mutiple_level_tag_filter=addAllDropDownNode(applicationContext,ComponentUpdater,
    dict_variable_application_data,
    true,
    reDrawLegend,
    node_function,
    link_function,
    recomputeDisplayedElement,
    size_of_draw_zone
  )
  // Popover containing the selector of nodes aggregation
  const detail_level=
      <Popover id='popover-details-level' style={{maxWidth:'100%',zIndex:1}}>
        <Popover.Header as="h3">{applicationContext.t('Banner.ndd')}</Popover.Header>
        <Popover.Body style={{maxHeight:'600px',overflowY:'auto',  marginLeft: '5px', width: '350px' }}>
          <FormGroup as={Row}>
            <Col xs={9}>
              {t('Menu.group')}
            </Col>
          </FormGroup>
          <>{(Object.entries(dict_variable_application_data.data.levelTags).length > 0) ? (<>
            {mutiple_level_tag_filter}</>
          ) : (<>
            <Form.Control placeholder="Pas de filtrage" style={{ opacity: opacity_advanced, color: '#6c757d' }} disabled /></>)}</>
        </Popover.Body>
      </Popover>


  const {filter}=dict_variable_application_data.data.display_style
  const addon_link_visual_filter_toolbar=[] as JSX.Element[]
  const displayFNL= checkboxFreeNullLink(dict_variable_application_data,t,node_function,link_function)
  if (applicationContext.has_sankey_suite) {
    const {units_names} = dict_variable_application_data.data
    let units = undefined
    if (units_names  && units_names.length > 0 && units_names[0].length > 0) {
      units = [units_names[0][0], 'Naturelles']
      sankey_menus['unité'] = additional_selector(
        dict_variable_application_data.data,
                dict_variable_application_data.set_data as (d:AFMSankeyData)=>void,
                units
      )
    }
    addon_link_visual_filter_toolbar.push(displayFNL)
  }
  if (!window.SankeyToolsStatic || window.sankey.toolbar) {
    const toolbar = <OpenSankeyToolbarBuilder
      applicationContext={applicationContext}
      dict_variable_application_data={dict_variable_application_data}
      dict_variable_elements_selected={dict_variable_elements_selected}
      filter={filter}
      set_current_filter={func_current_filter}
      detail_level={detail_level}
      url_prefix={applicationContext.has_sankey_suite ? 'toto' :''}
      first_selected_node={dict_variable_elements_selected.first_selected_node}
      GetSankeyMinWidthAndHeight={size_of_draw_zone}
      dict_hook_ref_setter_show_dialog_components={dict_hook_ref_setter_show_dialog_components}
      never_see_again={never_see_again}
      additional_link_visual_filter_content={addon_link_visual_filter_toolbar}
      reDrawLegend={reDrawLegend}
      node_function={node_function}
      link_function={link_function}
      recomputeDisplayedElement={recomputeDisplayedElement}
      ComponentUpdater={ComponentUpdater}
    />
    sankey_menus['toolbar']=toolbar
  }
  const menuTopTabView= SankeyPlusBannerView(
    dict_variable_application_data as SankeyPlusApplicationDataType,
    applicationContext.t, applicationContext.has_open_sankey_plus,
    _load_json,
    _load_json_catalog,
    dict_hook_ref_setter_show_dialog_components,
    suite_convert_data,
    selector_of_view
  )
  if(dict_variable_application_data.master_data && 
     dict_variable_application_data.master_data.view.length!==0 ){
    sankey_menus['view']=menuTopTabView
  }
  // Add a nav item the navbar to create and edit unitary sankey
  // Work similarly to the view nav item
  // Is added only if we have sankeydev
  // const sankeydevBanner=SankeyDevBannerUnit(
  //   dict_variable_application_data.data,
  //   dict_variable_application_data.set_data as (_:SankeyDevData)=>void,
  //   dict_variable_application_data.view, dict_variable_application_data.set_view,
  //   dict_variable_application_data.master_data as SankeyDevData,
  //   dict_variable_application_data.set_master_data as (_:SankeyDevData)=>void,
  //   dict_variable_elements_selected.multi_selected_nodes,
  //   applicationContext.t,
  //   applicationContext.has_open_sankey_plus,
  //   dict_hook_ref_setter_show_dialog_components,
  //   selector_of_view
  // )

  const modal_unitary_sankey_selector_link=<SankeyDevModalUnitarySector
    data={dict_variable_application_data.data}
    set_data={dict_variable_application_data.set_data}
    display_nodes={dict_variable_application_data.display_nodes}
    dict_hook_ref_setter_show_dialog_components={dict_hook_ref_setter_show_dialog_components}
    t={applicationContext.t}
    GetLinkValue={SuiteGetLinkValue}
  />

  const formatKeyHandler=(e:KeyboardEvent)=>{
    keyHandler(dict_variable_application_data,
      uiElementsRef,
      contextMenu,
      e,
      dict_variable_application_data.data,
      dict_variable_elements_selected,
      dict_variable_application_data.set_data,
      closeAllMenu,ref_alt_key_pressed,accept_simple_click,link_function,SuiteNodeTooltipContent,
      ComponentUpdater,dict_hook_ref_setter_show_dialog_components,applicationContext,
      node_function,
      applicationDraw
    )
    SankeyViewskeyHandler(
      applicationContext.t,
      e,
      dict_variable_application_data.view==='none',
      dict_variable_application_data,
      dict_variable_elements_selected,
      dict_hook_ref_setter_show_dialog_components,
      applicationContext.has_open_sankey_plus,
      set_view_not_saved,reDrawPlusLabels,ComponentUpdater,d_setter_input_value
    )
  }
  document.onkeydown = formatKeyHandler

  const additional_nav_item:JSX.Element[]=[]

  //TOAST That appeat to heavy processing func
  const toast_wait_func=<ToastWaitFunc
    dict_variable_application_data={dict_variable_application_data}
    dict_hook_ref_setter_show_dialog_components={dict_hook_ref_setter_show_dialog_components}
    applicationContext={applicationContext}
  />


  // MENU DRAGGABLE NODE ATTR
  const menu_node_attr=menu_configuration_nodes_attributes
  const menuNodeAttr= MenuDraggable(
    dict_hook_ref_setter_show_dialog_components,
    'ref_setter_show_menu_node_apparence',
    menu_node_attr,
    contextMenu.pointer_pos,applicationContext.t('Menu.Noeuds')+' '+applicationContext.t('Noeud.apparence.apparence')
  )

  // MENU DRAGGABLE NODE IO
  const menu_node_io = OpenSankeyMenuConfigurationNodesIO(
    applicationContext,
    dict_variable_application_data,
    dict_variable_elements_selected,
    SuiteGetLinkValue,
    node_function,link_function,
    ComponentUpdater,
    true
  )

  const menuNodeIO= MenuDraggable(
    dict_hook_ref_setter_show_dialog_components,
    'ref_setter_show_menu_node_io',
    menu_node_io,
    contextMenu.pointer_pos,
    applicationContext.t('Menu.Noeuds')+' '+applicationContext.t('Noeud.PF.PF')
  )

  // MENU DRAGGABLE NODE Tooltip editor
  const menu_node_tooltip = SankeyMenuConfigurationNodesTooltip(
    applicationContext,
    dict_variable_elements_selected,
    true)[0]

  const menuNodeTooltip= MenuDraggable(
    dict_hook_ref_setter_show_dialog_components,
    'ref_setter_show_menu_node_tooltip',
    menu_node_tooltip,
    contextMenu.pointer_pos,
    applicationContext.t('Menu.Noeuds')+' '+applicationContext.t('Noeud.IS')
  )


  // MENU DRAGGABLE NODE tag selection
  const menu_node_tags = SankeyMenuConfigurationNodesTags(applicationContext,dict_variable_application_data,dict_variable_elements_selected,node_function,ComponentUpdater,true)

  const menuNodeTags= MenuDraggable(
    dict_hook_ref_setter_show_dialog_components,
    'ref_setter_show_menu_node_tags',
    menu_node_tags,
    contextMenu.pointer_pos,
    applicationContext.t('Menu.Noeuds')+' '+applicationContext.t('Menu.Etiquettes')
  )

  // OpenSankey+ : cast of dict_hook_ref_setter_show_dialog_components in dict_hook_ref_setter_show_dialog_componentsType
  // MENU DRAGGABLE NODE ICON
  const additional_context_node_menu=[] as JSX.Element[]
  const additional_context_node_other=[]
  if(applicationContext.has_open_sankey_plus){
    // additional_context_node_menu.push(
    //   ContextNodeIcon(
    //     contextMenu,
    //     dict_hook_ref_setter_show_dialog_components,
    //     applicationContext.t
    //   )
    // )
    additional_context_node_other.push(
      context_node_view_node_unitary(
        applicationContext,
        dict_variable_application_data as SankeyDevApplicationDataType,
        contextMenu
      )
    )
  }

  // Array containaing additional button to add to the menu transformation
  let apply_transformation_additional_elements : JSX.Element[] = []
  const plus_diagram_selector= SankeyPlusDiagramSelector(
    dict_variable_application_data.master_data as SankeyPlusData,
    dict_variable_application_data.set_master_data as (_:SankeyPlusData|undefined)=>void,
    dict_variable_application_data.view,
    view_selected,set_view_selected,
    dict_variable_application_data.get_default_data
  )
  let DiagramSelector = OpenSankeyDiagramSelector
  const component_apply_transfor_OSP= apply_transformation_opensankey_plus_elements(
    dict_variable_application_data.master_data?
      dict_variable_application_data.master_data:
      dict_variable_application_data.data,
    applicationContext.t,
    elementToDispose
  )
  if (applicationContext.has_open_sankey_plus) {
    // Add buttons in the menu transformation for adding ZDT and views as variable transferable in SuiteUpdateLayout
    apply_transformation_additional_elements = component_apply_transfor_OSP

    DiagramSelector = plus_diagram_selector
  }

  const context_n = <OpenSankeyContextMenuNode
    applicationContext = {applicationContext}
    dict_variable_application_data = {dict_variable_application_data}
    dict_variable_elements_selected = {dict_variable_elements_selected}
    contextMenu = {contextMenu}
    dict_hook_ref_setter_show_dialog_components = {dict_hook_ref_setter_show_dialog_components}
    agregation = {agregation}
    node_function={node_function}
    link_function={link_function}
    additional_context_element_menu = {additional_context_node_menu}
    additional_context_element_other = {additional_context_node_other}
    ComponentUpdater={ComponentUpdater}
  />

  const config_link_data=MenuConfigurationLinksData(
    dict_variable_application_data,
    dict_variable_elements_selected,
    applicationContext,
    additional_data_element,
    ComponentUpdater,
    node_function,
    link_function
  )
  // MENU DRAGGABLE LINK DATA
  // MENU DRAGGABLE LINK DATA
  const menu_link_data = config_link_data

  const menuLinkData = MenuDraggable(
    dict_hook_ref_setter_show_dialog_components,
    'ref_setter_show_menu_link_data',
    menu_link_data,
    contextMenu.pointer_pos,
    applicationContext.t('Menu.flux')+' '+applicationContext.t('Flux.data.données'),
  )

  // MENU DRAGGABLE LINK APPEARENCE
  const menu_link_appearence = MenuConfigurationLinksAppearence(
    dict_variable_application_data,
    dict_variable_elements_selected,
    applicationContext,
    add_link_appearence_item,
    false,
    link_function,ComponentUpdater)

  const menuLinkAppearence = MenuDraggable(
    dict_hook_ref_setter_show_dialog_components,
    'ref_setter_show_menu_link_appearence',
    menu_link_appearence,
    contextMenu.pointer_pos,
    applicationContext.t('Menu.flux')+' '+applicationContext.t('Flux.apparence.apparence')
  )
  // MENU DRAGGABLE LINK Tooltip
  const menu_link_tooltip = MenuConfigurationLinksTooltip(
    data,set_data,
    dict_variable_elements_selected.multi_selected_links,
    applicationContext.t,true)

  const menuLinkTolltip = MenuDraggable(
    dict_hook_ref_setter_show_dialog_components,
    'ref_setter_show_menu_link_tooltip',
    menu_link_tooltip,
    contextMenu.pointer_pos,
    applicationContext.t('Menu.flux')+' '+applicationContext.t('Flux.IB'),
  )

  // MENU DRAGGABLE Link tag selection
  const menu_link_tags = MenuConfigurationLinksTags(dict_variable_application_data,
    dict_variable_elements_selected,
    applicationContext,
    true,
    ComponentUpdater,
    node_function,link_function)

  const menuLinkTags= MenuDraggable(
    dict_hook_ref_setter_show_dialog_components,
    'ref_setter_show_menu_link_tags',
    menu_link_tags,
    contextMenu.pointer_pos,
    applicationContext.t('Menu.flux')+' '+applicationContext.t('Menu.Etiquettes')
  )

  const context_l = <OpenSankeyContextMenuLink
    applicationContext = {applicationContext}
    dict_variable_application_data = {dict_variable_application_data}
    dict_variable_elements_selected = {dict_variable_elements_selected}
    contextMenu = {contextMenu}
    dict_hook_ref_setter_show_dialog_components = {dict_hook_ref_setter_show_dialog_components}
    node_function={node_function}
    link_function={link_function}
    ComponentUpdater={ComponentUpdater}
  />

  // MENU DRAGGABLE LAYOUT
  const menuLayout = MenuDraggable(
    dict_hook_ref_setter_show_dialog_components,
    'ref_setter_show_menu_layout',
    menu_configuration_layout,
    contextMenu.pointer_pos,
    applicationContext.t('Menu.MEP'),33
  )

  const context_for_zdd = <OpenSankeyContextMenuZDD
    applicationContext = {applicationContext}
    dict_variable_application_data = {dict_variable_application_data}
    contextMenu = {contextMenu}
    dict_hook_ref_setter_show_dialog_components = {dict_hook_ref_setter_show_dialog_components}
    node_function={node_function}
    link_function={link_function}
    reDrawLegend={reDrawLegend}
    ComponentUpdater={ComponentUpdater}
  />

  // MENU DRAGGABLE ZDT
  const menuDraggableZDT =  (MenuDraggable as unknown as SuiteMenuDraggableFType)(
    dict_hook_ref_setter_show_dialog_components,
    'ref_setter_show_menu_zdt',
    modifcation_zdt_component,
    contextMenu.pointer_pos,
    applicationContext.t('Menu.LL')
  )

  const context_for_zdt = context_zdt(
    contextMenu,
    applicationContext.t,
    dict_variable_application_data,
    dict_hook_ref_setter_show_dialog_components,
    dict_variable_elements_selected,ComponentUpdater,reDrawPlusLabels
  )

  const context_for_tag_legend = <ContextLegendTags
    applicationContext = {applicationContext}
    dict_variable_application_data = {dict_variable_application_data}
    dict_variable_elements_selected = {dict_variable_elements_selected}
    contextMenu = {contextMenu}
    GetLinkValue = {SuiteGetLinkValue}
    ComponentUpdater={ComponentUpdater}

  />

  const modal_import_icons = modal_selection_icons(
    applicationContext.t,
    dict_variable_elements_selected.multi_selected_nodes,
    dict_variable_application_data.data,
    dict_hook_ref_setter_show_dialog_components,
    node_function
  )

  // const menu_node_icon = SankeyPlusNodeIcon(
  //   applicationContext.t,
  //   dict_variable_application_data.data,
  //   dict_variable_application_data.set_data as (_:SankeyPlusData)=>void,
  //   dict_variable_elements_selected.multi_selected_nodes,
  //   applicationContext.has_open_sankey_plus,true,dict_hook_ref_setter_show_dialog_components
  // )
  // const menuNodeIcon= (MenuDraggable as SuiteMenuDraggableFType)(
  //   dict_hook_ref_setter_show_dialog_components,
  //   'ref_setter_show_menu_node_icon',
  //   menu_node_icon,
  //   contextMenu.pointer_pos,
  //   applicationContext.t('Menu.Noeuds')+' '+applicationContext.t('Noeud.icon.icon')
  // )

  // Modal to create unitary views after opening an excel file
  const module_dialogs : JSX.Element[] = []
  if (applicationContext.has_sankeydev) {
    sankeyDevDialogs(
      applicationContext,
      dict_variable_application_data,
      processFunctions,
      applicationDraw,
      convert_data,
        processTrade as unknown as (sankey_data: SankeyDevData) => void,
        dict_hook_ref_setter_show_dialog_components
    ).forEach(el=>module_dialogs.push(el))
  }
  module_dialogs.push(menuNodeAttr)
  module_dialogs.push(menuNodeIO)
  module_dialogs.push(menuNodeTooltip)
  module_dialogs.push(menuNodeTags)
  // module_dialogs.push(menuNodeIcon)
  module_dialogs.push(menuLinkAppearence)
  module_dialogs.push(menuLinkData)
  module_dialogs.push(menuLinkTolltip)
  module_dialogs.push(menuLinkTags)
  module_dialogs.push(menuLayout)
  module_dialogs.push(menuDraggableZDT)
  module_dialogs.push(context_n)
  module_dialogs.push(context_l)
  module_dialogs.push(context_for_zdd)
  module_dialogs.push(context_for_zdt)
  module_dialogs.push(context_for_tag_legend)
  module_dialogs.push(modal_unitary_sankey_selector_link)
  module_dialogs.push(modal_resolution_png)
  module_dialogs.push(modal_import_icons)

  const toast_view=view_toast(dict_hook_ref_setter_show_dialog_components)
  const toast_view_update=view_toast_update_view(dict_hook_ref_setter_show_dialog_components)

  if(applicationContext.has_open_sankey_plus){
    module_dialogs.push(toast_view)
    module_dialogs.push(toast_view_update)
  }



  useEffect(() => {
    if (animating.current) {
      return
    }
    d3.selectAll(' .opensankey #svg #g_label').remove()
    d3.selectAll(' .opensankey #svg #g_label_handles').remove()

    // Insert la balise qui contient tous les lables libres avant la balise de la légende
    d3.select('.opensankey #svg').insert('g','#g_links').attr('class','g_label').attr('id','g_label')
    d3.select('.opensankey #svg').append('g').attr('class','g_label_handles').attr('id','g_label_handles')
    // Call the function that add nodes to the sankey
    DrawAllNodes(contextMenu,
      dict_variable_application_data,
      uiElementsRef,
      dict_variable_elements_selected,
      applicationContext,
      ref_alt_key_pressed,
      accept_simple_click,link_function,
      SuiteNodeTooltipContent,
      ComponentUpdater,
      dict_hook_ref_setter_show_dialog_components,
      node_function,
      size_of_draw_zone,
      applicationDraw
    )
    // Dessine les éléments pris en comptes par Sankey+ même si l'user n'a pas activé cette license, cependant il ne peut pas les modifier
    SankeyPlusDrawNodesIllustration(
      data,
      Object.values(display_nodes),
      dict_variable_elements_selected,
      SuiteNodeTooltipContent,
      SuiteGetLinkValue,applicationContext.t
    )

    PlusNodeClickEvent(
      dict_variable_application_data as SankeyPlusApplicationDataType,
      dict_variable_elements_selected,
      uiElementsRef,
      animating,
      accept_simple_click,
      SuiteGetLinkValue,
      ComponentUpdater,
      Object.values(display_nodes)
    )

    PlusNodeDragEvent(
      dict_variable_application_data as SankeyPlusApplicationDataType,
      dict_variable_elements_selected as PlusElementsSelectedType,
      applicationContext,
      ref_alt_key_pressed.current,
      SuiteSankeyLinkText as LinkTextFuncType,
      SuiteGetLinkValue,
      size_of_draw_zone,ComponentUpdater,
      node_function,
      link_function,
      applicationDraw
    )

    PlusDrawNodesFO(
      data,
      display_nodes,
      dict_variable_elements_selected,
      SuiteNodeTooltipContent,
      SuiteGetLinkValue,applicationContext.t
    )
    SankeyDevDrawNodesNotToScale(dict_variable_application_data as SankeyDevApplicationDataType,Object.values(data.nodes))


    // Call the function that add links to the sankey
    d3.selectAll(' .opensankey #svg #sankey_def').remove()
    d3.select(' .opensankey #svg').append('defs').attr('id', 'sankey_def')

    DrawAllLinks(contextMenu,
      dict_variable_application_data,
      uiElementsRef,
      dict_variable_elements_selected,
      applicationContext,
      ref_alt_key_pressed,
      'relative',
      link_function,
      ComponentUpdater,
      dict_hook_ref_setter_show_dialog_components
    )

    PlusDrawLabels(
      dict_variable_application_data as SankeyPlusApplicationDataType,
      dict_variable_elements_selected as PlusElementsSelectedType,
      uiElementsRef,
      contextMenu,
      applicationContext,
      d_setter_input_value,
      size_of_draw_zone,
      start_point,
      contextMenu.closeAllMenuContext,
      ComponentUpdater,
      Object.values(data.labels),
      reDrawPlusLabels,
      link_function,
      applicationDraw
    )
    // Create traduction function
    DrawLegend(
      dict_variable_application_data,
      applicationContext,
      contextMenu,
      GetLinkValue,
      legend_clicked,ComponentUpdater,
      reDrawLegend,
      suiteResizeCanvas
    )
    if(data.unitary_node.length>0){
      SankeyDev_legend_elements(applicationContext.t,data)
    }

    // Zoom Behavior
    const svgSankey = d3.select('.opensankey #svg');
    (svgSankey as d3.Selection<Element, unknown, HTMLElement, unknown>)
      .call(d3.zoom()
        .filter(ev => { // Permet d'obliger Crtl pour activer le zoom
          return (ev.ctrlKey || ev.metaKey) && ev.buttons === 0
        })
        .wheelDelta(ev => { // Permet de regler la vitesse du zoom
          return -ev.deltaY * (ev.deltaMode === 1 ? 0.05 : ev.deltaMode ? 1 : 0.002)
        })
        .on('zoom', function (evt) {
          ZoomFunction(evt,dict_variable_application_data,size_of_draw_zone)
          sankey_plus_zoom_text_zone(evt)
        }))
      .on('dblclick.zoom', null)
    SetSvgBg(data)
    AdjustSankeyZone(dict_variable_application_data,size_of_draw_zone)

  },[animating,
    data,
    position,
    has_sankey_suite,
    size_of_draw_zone,
    SuiteGetLinkValue,
    has_sankeydev])

  return (<ChakraProvider theme={opensankey_theme}>
    <div style={{ 'backgroundColor' : 'WhiteSmoke' }}>
      <div className='div-Menu' style={{ 'backgroundColor' : 'WhiteSmoke'}} >
        <>
          <OpenSankeyMenu
            applicationContext={applicationContext}
            dict_variable_elements_selected={dict_variable_elements_selected}
            dict_variable_application_data={dict_variable_application_data}
            uiElementsRef={uiElementsRef}
            contextMenu={contextMenu}
            processFunctions={processFunctions}
            dict_hook_ref_setter_show_dialog_components={dict_hook_ref_setter_show_dialog_components}
            applicationDraw={applicationDraw}
            example_menu={<></>}
            configurations_menus={configurations_menus}
            menus={sankey_menus}
            cardsTemplate={cardsTemplate}
            external_modal={external_menu_modal}
            Reinitialization={Reinitialization}
            formations_menu={formations_menu}
            additional_nav_item={additional_nav_item}
            convert_data={suite_convert_data}
            elementToDispose={elementToDispose}
            apply_transformation_additional_elements={apply_transformation_additional_elements}
            DiagramSelector={DiagramSelector}
            callback={processTrade as callbackFuncType}
            ref_alt_key_pressed={ref_alt_key_pressed}
            accept_simple_click={accept_simple_click}
            link_function={link_function}
            NodeTooltipsContent={SuiteNodeTooltipContent}
            ComponentUpdater={ComponentUpdater}
            node_function={node_function}
          />

          {/* New way to save the dict_variable_application_data.data,it save master dict_variable_application_data.data if the displayed sankey is a view or dict_variable_application_data.data if it's the master dict_variable_application_data.data */}

          { applicationContext.has_sankey_suite ?  (
            <SplitVerticalModal
              data = {dict_variable_application_data.data}
              initial_trade_node_names = {
                (dict_variable_application_data.data as SuiteSankeyData).trade_sectors ?
                  (dict_variable_application_data.data as SuiteSankeyData).trade_sectors : []
              }
              ref_setter_show_split_trade = {
                dict_hook_ref_setter_show_dialog_components.ref_setter_show_split_trade_dialog
              } />
          ) :(<div/>)}
          {applicationContext.has_sankey_suite ? (
            <SupplyUseModelisationProd
              action_type={action_type}
              ref_setter_show_reconciliation={
                dict_hook_ref_setter_show_dialog_components.ref_setter_show_reconciliation
              }
              sankey_data={dict_variable_application_data.data}
              set_sankey_data={dict_variable_application_data.set_data}
              callback = {processTrade}
              DefaultSankeyData = {dict_variable_application_data.get_default_data}/>
          ) :(<div />)}
        </>
      </div>
      {module_dialogs.map(e=>e)}
      <>
        {//Ajout d'un delay pour laisser le temps au Menu de render pour ensuite utiliser sa hauteur afin d'ajouter un margin top au draw
        }


        <OpenSankeyDraw
          contextMenu={contextMenu}
          dict_variable_application_data={dict_variable_application_data}
          animation={animating}
          dict_variable_elements_selected={dict_variable_elements_selected}
          display_nodes={display_nodes}
          display_links={display_links}
          agregation={agregation}
          ref_alt_key_pressed={ref_alt_key_pressed}
          GetSankeyMinWidthAndHeight={size_of_draw_zone}
        />
      </>
    </div>
    {toast_wait_func}
  </ChakraProvider>)
}


export default SankeyApp



export const toast_warning_limit_node=
(dict_hook_ref_setter_show_dialog_components:SankeySuiteShowMenuComponentsType)=>{
  const show_toast=useState(false)
  dict_hook_ref_setter_show_dialog_components.ref_setter_show_toast_limit_node.current=show_toast[1]

  return <Toast key={'limit_node'}  show={show_toast[0]} onClose={()=>show_toast[1](false)} autohide bg='warning' className='toastWarningLimitNode' style={{ 'position': 'absolute', 'marginTop': '300px', 'marginLeft': '250px', 'zIndex': 1 }}>
    <Toast.Header  closeButton={false}> <small className='me-auto'>Attention</small> </Toast.Header>
    <Toast.Body>Nombre limite de noeud atteint pour les utilisateurs non connecté</Toast.Body>
  </Toast>}